
import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { checkRoutePermission } from "@/utils/routePermissions";
import { useUser } from "@/contexts/UserContext";
import { useToast } from "@/components/ui/use-toast";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { userId, role, loading } = useUser();
  const location = useLocation();
  const { toast } = useToast();

  const isAuthenticated = !!userId;
  // A signed-in user with no user_roles row (provisioning failed) gets the
  // lowest role for route-gating; otherwise the denied->/dashboard redirect
  // would loop forever. Real authorization lives in the database, not here.
  const effectiveRole = role ?? (isAuthenticated ? "user" : null);
  const hasPermission = isAuthenticated && checkRoutePermission(location.pathname, effectiveRole);

  useEffect(() => {
    if (!loading && isAuthenticated && !hasPermission) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page",
        variant: "destructive",
      });
    }
  }, [loading, isAuthenticated, hasPermission, location.pathname, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to auth page if not logged in
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!hasPermission) {
    // Redirect to dashboard if not authorized
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
